import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileText, Calendar, AlertCircle, CheckSquare, Square, Clock, FileCheck } from 'lucide-react';
import api from '../../lib/api';
import Chip from '../Chip';
import {
  StandardTable,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableBody,
  StandardTableRow,
  StandardTableCell,
} from '../StandardTable';
import TruncatedText from '../TruncatedText';
import { getPositionColorClasses } from '../../utils/positionColors';
import { getBillLinkClasses } from '../../utils/linkStyles';
import { Link } from 'react-router-dom';
import ReportNotesSection from '../reports/ReportNotesSection';
import ViewNotesModal from '../notes/ViewNotesModal';
import toast from 'react-hot-toast';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import LoadingList from '@shared/ui/LoadingList';
import ExportMenu from '../ExportMenu';
import ViewToggle from '../ViewToggle';
import { EditIcon } from '../icons';

interface OrgReportsTabProps {
  organizationId: string;
  organizationName: string;
  organizationLogoUrl?: string;
}

interface Workspace {
  id: string;
  name: string;
  description: string;
  bill_count: number;
}

interface ReportRecord {
  bill_id?: number;
  workspace_id: string;
  'Bill Number': string;
  'Bill Title': string;
  'Bill Closed': string;
  'Workspace Name': string;
  'Description': string;
  'Position': string;
  'Start Date': string;
  'End Date': string;
  'Action': string;
  notes?: Array<{
    id: string;
    title?: string;
    content: string;
    author: string;
    created_at: string;
    tags: string[];
  }>;
}

interface PositionStats {
  action_type: string;
  count: number;
  unique_bills: number;
}

const OrgReportsTab: React.FC<OrgReportsTabProps> = ({
  organizationId,
  organizationName,
  organizationLogoUrl
}) => {
  // Workspace selection state
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<string[]>([]);
  
  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // View mode and export state
  const [viewMode, setViewMode] = useState<'table' | 'card'>('card');
  
  // Notes modal state
  const [selectedNotesForModal, setSelectedNotesForModal] = useState<{
    notes: ReportRecord['notes'];
    billNumber: string;
    billTitle: string;
    billId?: number;
  } | null>(null);

  // Sorting state
  const [sortField, setSortField] = useState<'Workspace Name' | 'Bill Number' | 'Position' | 'Start Date' | 'End Date'>('Workspace Name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Fetch available workspaces
  const { data: workspaces = [], isLoading: workspacesLoading } = useQuery(
    ['org-workspaces', organizationId],
    async () => {
      const response = await api.get(`/org-reports/${organizationId}/workspaces`);
      return response.data.data || [];
    },
    {
      enabled: !!organizationId,
      onSuccess: (data) => {
        // Auto-select all workspaces on initial load
        if (selectedWorkspaces.length === 0 && data.length > 0) {
          setSelectedWorkspaces(data.map((w: Workspace) => w.id));
        }
      }
    }
  );

  // Fetch report data when workspaces are selected
  const { data: reportData = [], isLoading: reportLoading, error: reportError } = useQuery(
    ['org-sos-report', organizationId, selectedWorkspaces, startDate, endDate],
    async () => {
      if (selectedWorkspaces.length === 0) return [];
      
      const params = new URLSearchParams();
      params.append('workspace_ids', selectedWorkspaces.join(','));
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/org-reports/${organizationId}/sos-report?${params.toString()}`);
      return response.data.data || [];
    },
    {
      enabled: !!organizationId && selectedWorkspaces.length > 0
    }
  );

  // Fetch statistics
  const { data: statsData = [] } = useQuery(
    ['org-sos-stats', organizationId, selectedWorkspaces, startDate, endDate],
    async () => {
      if (selectedWorkspaces.length === 0) return [];
      
      const params = new URLSearchParams();
      params.append('workspace_ids', selectedWorkspaces.join(','));
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await api.get(`/org-reports/${organizationId}/sos-report/stats?${params.toString()}`);
      return response.data.data || [];
    },
    {
      enabled: !!organizationId && selectedWorkspaces.length > 0
    }
  );

  // Workspace selection handlers
  const handleSelectAll = () => {
    setSelectedWorkspaces(workspaces.map((w: Workspace) => w.id));
  };

  const handleDeselectAll = () => {
    setSelectedWorkspaces([]);
  };

  const handleToggleWorkspace = (workspaceId: string) => {
    setSelectedWorkspaces(prev => 
      prev.includes(workspaceId)
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  // Sort data
  const sortedData = React.useMemo(() => {
    if (!reportData || reportData.length === 0) return [];
    
    return [...reportData].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle special cases for dates
      if (sortField === 'End Date' || sortField === 'Start Date') {
        if (aVal === 'Ongoing') aVal = new Date();
        if (bVal === 'Ongoing') bVal = new Date();
        if (aVal && aVal !== 'Ongoing') aVal = new Date(aVal);
        if (bVal && bVal !== 'Ongoing') bVal = new Date(bVal);
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [reportData, sortField, sortDirection]);

  // Group data by workspace and bill for card view
  const groupedByWorkspaceAndBill = React.useMemo(() => {
    if (!reportData || reportData.length === 0) return [];
    
    const billMap = new Map<string, {
      billNumber: string;
      billTitle: string;
      billId?: number;
      billClosed: string;
      workspaceName: string;
      workspaceId: string;
      currentPosition: string;
      events: Array<{
        action: string;
        position: string;
        startDate: string;
        endDate: string;
        notes?: ReportRecord['notes'];
      }>;
      allNotes: ReportRecord['notes'];
    }>();
    
    // Sort records by start date - most recent first
    const sortedRecords = [...reportData].sort((a, b) => {
      // First sort by workspace name
      if (a['Workspace Name'] < b['Workspace Name']) return -1;
      if (a['Workspace Name'] > b['Workspace Name']) return 1;
      // Then by bill number
      if (a['Bill Number'] < b['Bill Number']) return -1;
      if (a['Bill Number'] > b['Bill Number']) return 1;
      // Then by date (most recent first)
      const aDate = a['Start Date'] ? new Date(a['Start Date']).getTime() : 0;
      const bDate = b['Start Date'] ? new Date(b['Start Date']).getTime() : 0;
      return bDate - aDate;
    });
    
    sortedRecords.forEach((record: ReportRecord) => {
      const key = `${record.workspace_id}-${record['Bill Number']}`;
      
      if (!billMap.has(key)) {
        billMap.set(key, {
          billNumber: record['Bill Number'],
          billTitle: record['Bill Title'],
          billId: record.bill_id,
          billClosed: record['Bill Closed'],
          workspaceName: record['Workspace Name'],
          workspaceId: record.workspace_id,
          currentPosition: record['Position'],
          events: [],
          allNotes: []
        });
      }
      
      const bill = billMap.get(key)!;
      
      bill.events.push({
        action: record['Action'],
        position: record['Position'],
        startDate: record['Start Date'],
        endDate: record['End Date'],
        notes: record.notes
      });
      
      if (record.notes && record.notes.length > 0) {
        bill.allNotes = [...(bill.allNotes || []), ...record.notes];
      }
    });
    
    return Array.from(billMap.values());
  }, [reportData]);

  // Check if any records have notes
  const hasAnyNotes = React.useMemo(() => {
    return reportData.some((record: ReportRecord) => Array.isArray(record.notes) && record.notes.length > 0);
  }, [reportData]);

  const handleSort = (field: string) => {
    const sortableField = field as typeof sortField;
    if (sortField === sortableField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(sortableField);
      setSortDirection('asc');
    }
  };

  const handleExport = async (format: 'csv' | 'excel' | 'pdf' | 'word') => {
    if (selectedWorkspaces.length === 0) {
      toast.error('Please select at least one workspace');
      return;
    }
    try {
      const response = await api.post(
        `/org-reports/${organizationId}/export`,
        {
          workspaceIds: selectedWorkspaces,
          format,
          startDate: startDate || null,
          endDate: endDate || null,
          viewMode,
          organizationLogoUrl: organizationLogoUrl || null
        },
        { responseType: 'blob' }
      );

      // Create download link
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${organizationName.replace(/[^a-zA-Z0-9]/g, '_')}_SOS_Report.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (err: any) {
      console.error('Export failed:', err);
      toast.error('Failed to export report');
    }
  };

  const getActionColor = (action: string) => {
    switch (action?.toLowerCase()) {
      case 'bill_added':
        return 'bg-success-light text-success-dark dark:bg-success-dark/20 dark:text-success-light';
      case 'position_changed':
        return 'bg-primary-light text-primary-dark dark:bg-primary-dark/20 dark:text-primary-light';
      case 'tracking_stopped':
        return 'bg-destructive-light text-destructive-dark dark:bg-destructive-dark/20 dark:text-destructive-light';
      case 'tracking_resumed':
      case 'tracking_started':
        return 'bg-warning-light text-warning-dark dark:bg-warning-dark/20 dark:text-warning-light';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (workspacesLoading) {
    return (
      <LoadingList rows={4} label="Loading reports" />
    );
  }

  return (
    <div className="space-y-6">
      {/* Workspace Selection */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-600" />
            SOS Report - Select Workspaces
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSelectAll}
              className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Select All
            </button>
            <span className="text-text-tertiary">|</span>
            <button
              onClick={handleDeselectAll}
              className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
            >
              Deselect All
            </button>
          </div>
        </div>

        {workspaces.length === 0 ? (
          <p className="text-text-secondary dark:text-text-dark-secondary text-sm">
            No workspaces available for this organization.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {workspaces.map((workspace: Workspace) => (
              <button
                key={workspace.id}
                onClick={() => handleToggleWorkspace(workspace.id)}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                  selectedWorkspaces.includes(workspace.id)
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-border-light dark:border-border-dark hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                }`}
              >
                {selectedWorkspaces.includes(workspace.id) ? (
                  <CheckSquare className="h-5 w-5 text-primary-600 flex-shrink-0" />
                ) : (
                  <Square className="h-5 w-5 text-text-tertiary flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary truncate">
                    {workspace.name}
                  </p>
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    {workspace.bill_count || 0} tracked bills
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedWorkspaces.length > 0 && (
        <>
          {/* Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              {/* Organization Logo */}
              {organizationLogoUrl && (
                <img 
                  src={organizationLogoUrl} 
                  alt={`${organizationName} logo`}
                  className="h-12 w-auto object-contain"
                />
              )}
              <div>
                <h3 className="text-lg font-semibold text-left text-text-primary dark:text-text-dark-primary flex items-center gap-2">
                  <EditIcon className="h-5 w-5 text-info-600" />
                  {organizationName} - SOS Report
                </h3>
                <p className="text-sm text-left text-text-secondary dark:text-text-dark-secondary mt-1">
                  Position changes across {selectedWorkspaces.length} workspace{selectedWorkspaces.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex items-center space-x-2">
                <ViewToggle value={viewMode} onChange={setViewMode} />
              </div>
              
              {/* Export Dropdown */}
              <ExportMenu
                formats={
              viewMode === 'table'
                ? ['csv', 'excel', { format: 'pdf', note: 'Landscape' }]
                : [{ format: 'word', note: 'Portrait' }, { format: 'pdf', note: 'Portrait' }]
            }
                onExport={handleExport}
              />
            </div>
          </div>

          {/* Date Filters and Statistics */}
          <div className="card p-4">
            <div className="flex items-center justify-between gap-4 pb-4 border-b border-border-light dark:border-border-dark">
              {/* Date Range */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-text-tertiary" />
                  <span className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary">Date Range:</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-2 py-1 border border-border-light dark:border-border-dark rounded bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary text-sm"
                  />
                  <span className="text-text-tertiary text-sm">to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-2 py-1 border border-border-light dark:border-border-dark rounded bg-background-tertiary dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary text-sm"
                  />
                </div>

                <button
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                  }}
                  className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  Clear dates
                </button>
              </div>

              {/* Statistics */}
              {statsData.length > 0 && (
                <div className="flex items-center space-x-4 text-sm text-text-secondary dark:text-text-dark-secondary">
                  {statsData.map((stat: PositionStats) => (
                    <span key={stat.action_type} className="bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 px-3 py-1 rounded-full text-xs font-medium">
                      {stat.count || 0} {stat.action_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} ({stat.unique_bills || 0} bills)
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Report Data */}
          <div className="card p-0 overflow-hidden">
            {reportLoading ? (
              <div className="flex items-center justify-center h-32">
                <LoadingSpinner size="lg" />
              </div>
            ) : reportError ? (
              <div className="p-8 text-center">
                <AlertCircle className="h-12 w-12 text-destructive-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
                  Error Loading Report
                </h3>
                <p className="text-text-secondary dark:text-text-dark-secondary">
                  Failed to load position tracking report. Please try again.
                </p>
              </div>
            ) : reportData.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="h-12 w-12 text-text-tertiary mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
                  No position changes found
                </h3>
                <p className="text-text-secondary dark:text-text-dark-secondary">
                  No position changes found for the selected workspaces and date range.
                </p>
              </div>
            ) : (
              <div>
                {/* Table View */}
                {viewMode === 'table' && (
                  <StandardTable>
                    <StandardTableHeader>
                      <StandardTableRow>
                        <StandardTableHeaderCell
                          sortable
                          sortKey="Workspace Name"
                          currentSortKey={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          columnType="auto"
                        >
                          Workspace
                        </StandardTableHeaderCell>
                        <StandardTableHeaderCell
                          sortable
                          sortKey="Bill Number"
                          currentSortKey={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          columnType="auto"
                        >
                          Bill Number
                        </StandardTableHeaderCell>
                        <StandardTableHeaderCell columnType="fit" responsiveHide="lg">
                          Bill Closed
                        </StandardTableHeaderCell>
                        <StandardTableHeaderCell
                          sortable
                          sortKey="Position"
                          currentSortKey={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          columnType="fit"
                        >
                          Position
                        </StandardTableHeaderCell>
                        <StandardTableHeaderCell
                          sortable
                          sortKey="Start Date"
                          currentSortKey={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          columnType="fit"
                          responsiveHide="lg"
                        >
                          Start Date
                        </StandardTableHeaderCell>
                        <StandardTableHeaderCell
                          sortable
                          sortKey="End Date"
                          currentSortKey={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                          columnType="fit"
                          responsiveHide="lg"
                        >
                          End Date
                        </StandardTableHeaderCell>
                        <StandardTableHeaderCell columnType="fit">
                          Action
                        </StandardTableHeaderCell>
                        {hasAnyNotes && (
                          <StandardTableHeaderCell columnType="expand">
                            Notes
                          </StandardTableHeaderCell>
                        )}
                      </StandardTableRow>
                    </StandardTableHeader>
                    <StandardTableBody>
                      {sortedData.map((record: ReportRecord, index: number) => {
                        const recordNotes = Array.isArray(record.notes) ? record.notes : [];
                        
                        return (
                          <StandardTableRow key={index}>
                            <StandardTableCell>
                              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                                {record['Workspace Name']}
                              </span>
                            </StandardTableCell>
                            <StandardTableCell>
                              <div className="min-w-0">
                                <div className="text-sm font-medium mb-1">
                                  {record.bill_id ? (
                                    <Link 
                                      to={`/legislation/${record.bill_id}`}
                                      className={getBillLinkClasses()}
                                    >
                                      {record['Bill Number']}
                                    </Link>
                                  ) : (
                                    <span className="text-text-primary dark:text-text-dark-primary">{record['Bill Number']}</span>
                                  )}
                                </div>
                                <TruncatedText 
                                  text={record['Bill Title']} 
                                  lines={2}
                                  className="text-xs text-text-secondary dark:text-text-dark-secondary break-words"
                                />
                              </div>
                            </StandardTableCell>
                            <StandardTableCell responsiveHide="lg">
                              <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                                {record['Bill Closed'] === 'Active' ? (
                                  <span className="text-muted-foreground">Active</span>
                                ) : (
                                  record['Bill Closed']
                                )}
                              </div>
                            </StandardTableCell>
                            <StandardTableCell>
                              {record['Position'] ? (
                                <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(record['Position'])}`}>
                                  {record['Position']}
                                </span>
                              ) : (
                                <span className="text-text-tertiary dark:text-text-dark-tertiary text-sm">-</span>
                              )}
                            </StandardTableCell>
                            <StandardTableCell responsiveHide="lg">
                              <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                                {record['Start Date']}
                              </div>
                            </StandardTableCell>
                            <StandardTableCell responsiveHide="lg">
                              <div className="text-sm text-text-secondary dark:text-text-dark-secondary">
                                {record['End Date']}
                              </div>
                            </StandardTableCell>
                            <StandardTableCell>
                              {record['Action'] ? (
                                <Chip
                                  label={record['Action'].replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  variant="custom"
                                  className={getActionColor(record['Action'])}
                                />
                              ) : (
                                <span className="text-text-tertiary dark:text-text-dark-tertiary text-sm">-</span>
                              )}
                            </StandardTableCell>
                            {hasAnyNotes && (
                              <StandardTableCell>
                                {recordNotes.length > 0 ? (
                                  <div>
                                    {recordNotes.slice(0, 1).map((note) => (
                                      <div key={note.id}>
                                        <div className="bg-primary-50 dark:bg-primary-900/20 p-2 rounded border border-primary-200 dark:border-primary-800">
                                          {note.title && (
                                            <div className="font-medium text-text-primary dark:text-text-dark-primary text-xs mb-1 line-clamp-1">
                                              {note.title}
                                            </div>
                                          )}
                                          <div 
                                            className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-3 break-words prose prose-xs max-w-none"
                                            dangerouslySetInnerHTML={{ __html: note.content }}
                                          />
                                          <div className="mt-1 flex items-center gap-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                                            <span className="truncate">{note.author}</span>
                                            <span>•</span>
                                            <span>{new Date(note.created_at).toLocaleDateString()}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    {recordNotes.length > 1 && (
                                      <button
                                        onClick={() => setSelectedNotesForModal({
                                          notes: recordNotes,
                                          billNumber: record['Bill Number'],
                                          billTitle: record['Bill Title'],
                                          billId: record.bill_id
                                        })}
                                        className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 cursor-pointer"
                                      >
                                        +{recordNotes.length - 1} more (click to view all)
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-text-tertiary dark:text-text-dark-tertiary text-sm">No notes</span>
                                )}
                              </StandardTableCell>
                            )}
                          </StandardTableRow>
                        );
                      })}
                    </StandardTableBody>
                  </StandardTable>
                )}

                {/* Card View - Grouped by Workspace and Bill */}
                {viewMode === 'card' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                    {groupedByWorkspaceAndBill.map((bill, index: number) => {
                      const cardNotes = Array.isArray(bill.allNotes) ? bill.allNotes : [];
                      
                      return (
                        <div key={index} className="card p-4 hover:shadow-md transition-shadow">
                          {/* Workspace Badge */}
                          <div className="mb-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                              {bill.workspaceName}
                            </span>
                          </div>

                          {/* Bill Header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <FileCheck className="w-4 h-4 text-primary-500" />
                              <h3 className="text-lg font-semibold">
                                {bill.billId ? (
                                  <Link 
                                    to={`/legislation/${bill.billId}`}
                                    className={getBillLinkClasses()}
                                  >
                                    {bill.billNumber}
                                  </Link>
                                ) : (
                                  <span className="text-text-primary dark:text-text-dark-primary">{bill.billNumber}</span>
                                )}
                              </h3>
                            </div>
                            <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(bill.currentPosition)}`}>
                              {bill.currentPosition}
                            </span>
                          </div>
                          
                          <div className="mb-3">
                            <TruncatedText 
                              text={bill.billTitle} 
                              lines={2}
                              className="font-medium"
                            />
                          </div>

                          {/* Bill Status */}
                          <div className="flex items-center space-x-1 mb-3">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 text-warning-500" />
                            <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                              <span className="font-medium">Status: </span>
                              {bill.billClosed === 'Active' ? 'Active' : bill.billClosed}
                            </span>
                          </div>

                          {/* Timeline of Changes */}
                          <div className="border-l-2 border-primary-200 dark:border-primary-700 ml-2 pl-3 space-y-3">
                            {bill.events.map((event, eventIndex) => (
                              <div key={eventIndex} className="relative">
                                {/* Timeline dot */}
                                <div className={`absolute -left-[17px] top-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-background-dark-secondary ${
                                  event.action === 'bill_added' ? 'bg-success-500' :
                                  event.action === 'position_changed' ? 'bg-primary-500' :
                                  event.action === 'tracking_stopped' ? 'bg-error-500' :
                                  event.action === 'tracking_resumed' || event.action === 'tracking_started' ? 'bg-warning-500' :
                                  'bg-text-tertiary'
                                }`} />
                                
                                {/* Event content */}
                                <div className="text-xs">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <Chip
                                      label={event.action ? event.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}
                                      variant="custom"
                                      size="sm"
                                      className={getActionColor(event.action)}
                                    />
                                    {event.position && (
                                      <span className={`px-1.5 py-0.5 rounded text-xs ${getPositionColorClasses(event.position)}`}>
                                        {event.position}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 text-text-tertiary dark:text-text-dark-tertiary">
                                    <Clock className="w-3 h-3" />
                                    <span>
                                      {event.startDate && event.startDate !== 'Ongoing' 
                                        ? new Date(event.startDate).toLocaleDateString() 
                                        : event.startDate}
                                      {event.endDate && event.endDate !== 'Ongoing' && event.endDate !== event.startDate && (
                                        <> → {new Date(event.endDate).toLocaleDateString()}</>
                                      )}
                                      {event.endDate === 'Ongoing' && (
                                        <> → <span className="text-success-600 dark:text-success-400">Ongoing</span></>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Notes */}
                          {cardNotes.length > 0 && (
                            <ReportNotesSection 
                              notes={cardNotes}
                              entityName={bill.billNumber}
                              defaultExpanded={false}
                              className="mt-3"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* View Notes Modal */}
      {selectedNotesForModal && (
        <ViewNotesModal
          isOpen={!!selectedNotesForModal}
          onClose={() => setSelectedNotesForModal(null)}
          notes={(selectedNotesForModal.notes || []).map(note => ({
            id: note.id,
            title: note.title,
            content: note.content,
            created_at: note.created_at,
            updated_at: note.created_at,
            include_in_reports: true,
            author_name: note.author,
            tags: note.tags || []
          }))}
          entityType="bill"
          entityId={selectedNotesForModal.billId?.toString() || ''}
          entityDisplayName={`${selectedNotesForModal.billNumber} - ${selectedNotesForModal.billTitle}`}
          workspaceId=""
        />
      )}
    </div>
  );
};

export default OrgReportsTab;

